import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

export const VERDE_MARCA = '#0E7C5A';

const COLORES = [VERDE_MARCA, '#3B82F6', '#F59E0B', '#8B5CF6', '#EF4444', '#14B8A6'];

export function colorDe(i: number): string {
  return COLORES[i % COLORES.length] ?? VERDE_MARCA;
}

const EJES = { fontSize: 11, fill: '#9CA3AF' };

interface Globito {
  active?: boolean;
  label?: string | number;
  payload?: Array<{
    name?: string;
    value?: number;
    color?: string;
    payload?: { fill?: string };
  }>;
}

export function enDinero(valor: number): string {
  return `$${valor.toLocaleString('es-EC', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function enPorcentaje(valor: number, decimales = 1): string {
  return `${valor.toLocaleString('es-EC', {
    minimumFractionDigits: decimales,
    maximumFractionDigits: decimales,
  })} %`;
}

export function enMiles(valor: number): string {
  if (Math.abs(valor) >= 1000) {
    return `$${Math.round(valor / 1000)}k`;
  }
  return `$${Math.round(valor)}`;
}

function Globo({ active, payload, label }: Globito) {
  if (!active || !payload?.length) {
    return null;
  }

  return (
    <div className="rounded-xl border border-armazon-200 bg-white px-3 py-2 shadow-lg">
      <p className="mb-1 text-[10px] font-bold uppercase tracking-wide text-armazon-500">{label}</p>
      {payload.map((p) => (
        <p key={p.name} className="text-xs text-armazon-700">
          <span
            className="mr-1.5 inline-block size-2 rounded-full align-middle"
            style={{ backgroundColor: p.color ?? p.payload?.fill }}
          />
          {p.name}: <span className="font-semibold">{enDinero(p.value ?? 0)}</span>
        </p>
      ))}
    </div>
  );
}

export function Evolucion<T>({
  datos,
  claveX,
  claveY,
  nombre,
}: {
  datos: readonly T[];
  claveX: string;
  claveY: string;
  nombre: string;
}) {
  return (
    <ResponsiveContainer width="100%" height={230}>
      <AreaChart data={datos} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id="degradadoMarca" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={VERDE_MARCA} stopOpacity={0.28} />
            <stop offset="100%" stopColor={VERDE_MARCA} stopOpacity={0.02} />
          </linearGradient>
        </defs>
        <XAxis dataKey={claveX} tick={EJES} axisLine={false} tickLine={false} />
        <YAxis
          tick={EJES}
          axisLine={false}
          tickLine={false}
          width={52}
          tickFormatter={(v: number) => enMiles(v)}
        />
        <Tooltip content={<Globo />} />
        <Area
          type="monotone"
          dataKey={claveY}
          name={nombre}
          stroke={VERDE_MARCA}
          strokeWidth={2}
          fill="url(#degradadoMarca)"
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

export function Barras<T>({
  datos,
  claveEtiqueta,
  claveValor,
  nombre,
}: {
  datos: readonly T[];
  claveEtiqueta: string;
  claveValor: string;
  nombre: string;
}) {
  return (
    <ResponsiveContainer width="100%" height={Math.max(180, datos.length * 42)}>
      <BarChart data={datos as never} layout="vertical" margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
        <XAxis
          type="number"
          tick={EJES}
          axisLine={false}
          tickLine={false}
          tickFormatter={(v: number) => enMiles(v)}
        />
        <YAxis
          type="category"
          dataKey={claveEtiqueta}
          tick={EJES}
          axisLine={false}
          tickLine={false}
          width={122}
        />
        <Tooltip content={<Globo />} cursor={{ fill: '#F3F4F6' }} />
        <Bar dataKey={claveValor} name={nombre} radius={[0, 6, 6, 0]}>
          {datos.map((_, i) => (
            <Cell key={i} fill={colorDe(i)} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

export function BarrasApiladas<T>({
  datos,
  claveEtiqueta,
  series,
}: {
  datos: readonly T[];
  claveEtiqueta: string;
  series: ReadonlyArray<{ clave: string; nombre: string; color: string }>;
}) {
  return (
    <ResponsiveContainer width="100%" height={250}>
      <BarChart data={datos as never} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <XAxis dataKey={claveEtiqueta} tick={EJES} axisLine={false} tickLine={false} />
        <YAxis
          tick={EJES}
          axisLine={false}
          tickLine={false}
          width={46}
          tickFormatter={(v: number) => v.toLocaleString('es-EC')}
        />
        <Tooltip content={<GloboDeUnidades />} cursor={{ fill: '#F3F4F6' }} />
        <Legend
          verticalAlign="top"
          height={26}
          formatter={(valor: string) => (
            <span className="text-[11px] text-armazon-600">{valor}</span>
          )}
        />
        {series.map((s) => (
          <Bar key={s.clave} dataKey={s.clave} name={s.nombre} stackId="uno" fill={s.color} />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

export function BarrasAgrupadas<T>({
  datos,
  claveEtiqueta,
  series,
}: {
  datos: readonly T[];
  claveEtiqueta: string;
  series: ReadonlyArray<{ clave: string; nombre: string; color: string }>;
}) {
  return (
    <ResponsiveContainer width="100%" height={250}>
      <BarChart data={datos as never} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <XAxis dataKey={claveEtiqueta} tick={EJES} axisLine={false} tickLine={false} />
        <YAxis
          tick={EJES}
          axisLine={false}
          tickLine={false}
          width={52}
          tickFormatter={(v: number) => enMiles(v)}
        />
        <Tooltip content={<Globo />} cursor={{ fill: '#F3F4F6' }} />
        <Legend
          verticalAlign="top"
          height={26}
          formatter={(valor: string) => (
            <span className="text-[11px] text-armazon-600">{valor}</span>
          )}
        />
        {series.map((s) => (
          <Bar key={s.clave} dataKey={s.clave} name={s.nombre} fill={s.color} radius={[4, 4, 0, 0]} />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

function GloboDeUnidades({ active, payload, label }: Globito) {
  if (!active || !payload?.length) {
    return null;
  }

  return (
    <div className="rounded-xl border border-armazon-200 bg-white px-3 py-2 shadow-lg">
      <p className="mb-1 text-[10px] font-bold uppercase tracking-wide text-armazon-500">{label}</p>
      {payload.map((p) => (
        <p key={p.name} className="text-xs text-armazon-700">
          <span
            className="mr-1.5 inline-block size-2 rounded-full align-middle"
            style={{ backgroundColor: p.color }}
          />
          {p.name}:{' '}
          <span className="font-semibold">{Number(p.value ?? 0).toLocaleString('es-EC')}</span>
        </p>
      ))}
    </div>
  );
}

function GloboDeParte({ active, payload, total }: Globito & { total: number }) {
  const parte = payload?.[0];
  if (!active || !parte) {
    return null;
  }

  return (
    <div className="rounded-xl border border-armazon-200 bg-white px-3 py-2 shadow-lg">
      <p className="text-xs text-armazon-700">
        {parte.name}: <span className="font-semibold">{enDinero(parte.value ?? 0)}</span>
      </p>
      <p className="text-[10px] text-armazon-400">
        {enPorcentaje(((parte.value ?? 0) / total) * 100)} del total
      </p>
    </div>
  );
}

export function Reparto({ datos }: { datos: Array<{ nombre: string; valor: number }> }) {
  const conValor = datos.filter((d) => d.valor > 0);
  const total = conValor.reduce((suma, d) => suma + d.valor, 0);

  if (conValor.length === 0) {
    return (
      <p className="grid h-[230px] place-items-center text-xs uppercase text-armazon-400">
        Sin movimientos en el período
      </p>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={230}>
      <PieChart>
        <Pie
          data={conValor}
          dataKey="valor"
          nameKey="nombre"
          innerRadius={52}
          outerRadius={82}
          paddingAngle={2}
          stroke="none"
        >
          {conValor.map((d, i) => (
            <Cell key={d.nombre} fill={colorDe(i)} />
          ))}
        </Pie>
        <Tooltip
          content={<GloboDeParte total={total} />}
        />
        <Legend
          verticalAlign="bottom"
          height={28}
          formatter={(valor: string) => (
            <span className="text-[11px] text-armazon-600">{valor}</span>
          )}
        />
      </PieChart>
    </ResponsiveContainer>
  );
}
